import React, { Fragment, useState, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Button,
  CircularProgress,
  Chip,
  DialogContent,
  Typography,
  TextField,
  Tooltip
} from "@material-ui/core"
import Autocomplete from "@material-ui/lab/Autocomplete"
import LoaderButton from "../../../../../../../components/LoaderButton"
import {
  addEmailGroup,
  getEmailsWithTags,
  removeEmailGroup,
  removeEmail
} from "actions/organization"
import { openSnackBar } from "actions/snack-bar"
import { useFormik } from "formik"
import useStyles from "./styles"

const UpdateEmail = ({ selectedEmail, setSelectedEmail, toggleDialog }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { id, tags = [] } = useSelector(state => state.organization.currentOrg)
  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState([])
  const [removeEmailLoading, setRemoveEmailLoading] = useState(false)
  const [autocompleteRefresh, setAutocompleteRefresh] = useState(false)
  const [emailData, setEmailData] = useState(selectedEmail)
  const [tagValue, setTagValue] = useState("")

  const formik = useFormik({
    initialValues: {
      tags: []
    },
    onSubmit: values => {
      setLoading(true)
      dispatch(addEmailGroup(id, selectedEmail.id, values.tags))
        .then(response => {
          setAutocompleteRefresh(true)
          dispatch(getEmailsWithTags(id)).then(repsonse => {
            let updatedTags = []
            let tagIds
            let tagData = []
            let tagsObj = {}

            repsonse.data.emailsWithTags.forEach(email => {
              /* eslint-disable */
              if (email.id === selectedEmail.id) {
                tags.forEach(tag => {
                  tagsObj = {
                    ...tagsObj,
                    [Number(tag.id)]: { id: Number(tag.id), name: tag.name }
                  }
                })

                tagIds = email.tags.split(",")
                tagData = tagIds.map(tagId => ({
                  id: Number(tagId),
                  name: tagsObj[Number(tagId)]
                    ? tagsObj[Number(tagId)].name
                    : ""
                }))
                updatedTags = tagData
              }
              /* eslint-enable */
            })

            setEmailData(currentData => ({
              ...currentData,
              tagData: updatedTags
            }))
          })
          dispatch(openSnackBar({ message: "Tags added." }))
        })
        .catch(() => {
          dispatch(openSnackBar({ message: "Error adding email." }))
        })
        .finally(() => {
          setLoading(false)
          formik.resetForm()
          setAutocompleteRefresh(false)
        })
    }
  })

  const handleTagChange = (event, newValue) => {
    const newTags = newValue.map(value => value.id)
    formik.setFieldValue("tags", newTags)
  }

  const availableTags = useMemo(() => {
    let nonUsedTags = []
    if (emailData.tagData.length > 0) {
      const usedTags = emailData.tagData.map(tag => tag.id)
      tags.forEach(tag => {
        if (usedTags.indexOf(tag.id) === -1) {
          nonUsedTags.push(tag)
        }
      })
      return nonUsedTags.sort((a, b) => a.name.localeCompare(b.name))
    } else {
      return tags.sort((a, b) => a.name.localeCompare(b.name))
    }
  }, [emailData.tagData])

  const handleDelete = tagId => () => {
    setDeleteLoading(ids => [...ids, tagId])

    dispatch(removeEmailGroup(id, emailData.id, tagId))
      .then(() => {
        dispatch(openSnackBar({ message: "Email tag removed." }))
        dispatch(getEmailsWithTags(id)).then(repsonse => {
          let updatedTags = []
          let tagIds
          let tagData = []
          let tagsObj = {}

          repsonse.data.emailsWithTags.forEach(email => {
            /* eslint-disable */
            if (email.id === selectedEmail.id) {
              tags.forEach(tag => {
                tagsObj = {
                  ...tagsObj,
                  [Number(tag.id)]: { id: Number(tag.id), name: tag.name }
                }
              })

              tagIds = email.tags.split(",")
              tagData = tagIds.map(tagId => ({
                id: Number(tagId),
                name: tagsObj[Number(tagId)] ? tagsObj[Number(tagId)].name : ""
              }))
              updatedTags = tagData
            }
            /* eslint-enable */
          })

          setEmailData(currentData => ({
            ...currentData,
            tagData: updatedTags
          }))
        })
      })
      .catch(() => {
        dispatch(openSnackBar({ message: "Error removing email tag." }))
      })
      .finally(() => {
        setDeleteLoading(ids => {
          return ids.filter(id => id !== tagId)
        })
      })
  }

  const handleRemoveEmail = emailId => () => {
    setRemoveEmailLoading(true)
    dispatch(removeEmail(id, emailId))
      .then(response => {
        dispatch(openSnackBar({ message: "Email removed" }))
        dispatch(getEmailsWithTags(id)).catch(err => {
          console.log(err)
        })
      })
      .catch(() => {
        dispatch(openSnackBar({ message: "Error removing email" }))
      })
      .finally(() => {
        setRemoveEmailLoading(false)
        setSelectedEmail()
        toggleDialog()
      })
  }

  return (
    <Fragment>
      <DialogContent>
        <Typography variant="h5">{emailData.email}</Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Autocomplete
            key={autocompleteRefresh}
            className={classes.tagsInput}
            multiple
            size="small"
            options={availableTags}
            getOptionLabel={option => option.name}
            onChange={handleTagChange}
            disabled={loading}
            classes={{
              listbox: classes.listBox,
              popupIndicator: classes.popupIndicator,
              root: classes.root,
              tag: classes.rootTag
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                label="Add Tags"
                placeholder=""
                error={Boolean(formik.touched.tags && formik.errors.tags)}
                helperText={formik.touched.tags && formik.errors.tags}
              />
            )}
          />
          <Tooltip
            title={
              /* eslint-disable */
              availableTags.length === 0
                ? "All tags used"
                : formik.values.tags.length === 0
                ? "Select a Tag"
                : ""
              /* eslint-enable */
            }
          >
            <div>
              <LoaderButton
                disabled={
                  availableTags.length === 0 || formik.values.tags.length === 0
                }
                className={classes.addButton}
                onClick={formik.handleSubmit}
                working={loading}
                variant="contained"
                color="primary"
              >
                Add
              </LoaderButton>
            </div>
          </Tooltip>
        </Box>
        <Box className={classes.tagContainer}>
          <DialogContent className={classes.tagContent}>
            {emailData &&
              emailData.tagData.length !== 0 &&
              emailData.tagData.map(tag => (
                <Chip
                  key={tag.id}
                  className={classes.tag}
                  id={tag.id}
                  label={tag.name}
                  onDelete={handleDelete(tag.id)}
                  deleteIcon={
                    deleteLoading.indexOf(tag.id) !== -1 ? (
                      <CircularProgress
                        size={16}
                        classes={{
                          root: classes.deleteLoading,
                          colorPrimary: classes.spinnerColor
                        }}
                      />
                    ) : null
                  }
                />
              ))}
          </DialogContent>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <LoaderButton
            classes={{ button: classes.removeButton }}
            onClick={handleRemoveEmail(emailData.id)}
            working={removeEmailLoading}
            variant="text"
          >
            Remove email
          </LoaderButton>
        </Box>
      </DialogContent>
    </Fragment>
  )
}

export default UpdateEmail



// WEBPACK FOOTER //
// ./src/pages/Admin/pages/Communication/CommunicationEmail/ManageTagsDialog/UpdateEmail/index.jsx